import { useTranslation } from "react-i18next";
import type { User, ThemeClasses } from "../types";
import { UserGridCard } from "./UserGridCard";
import { UserListRow } from "./UserListRow";

interface UserListProps {
  users: User[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  selectedView: "grid" | "list";
  onDetails: (user: User) => void;
  onEdit: (user: User) => void;
  onDelete?: (user: User) => void;
  onHardDelete?: (user: User) => void;
  onActivate?: (user: User) => void;
  onChangePassword?: (user: User) => void;
  isAdminReseller?: boolean;
  isSelectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelection?: (user: User) => void;
}

export function UserList({
  users,
  themeClasses,
  isDarkMode,
  selectedView,
  onDetails,
  onEdit,
  onDelete,
  onHardDelete,
  onActivate,
  onChangePassword,
  isAdminReseller,
  isSelectionMode = false,
  selectedIds = new Set(),
  onToggleSelection,
}: UserListProps) {
  const { t } = useTranslation("adminSections");

  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div
          className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
            isDarkMode ? "bg-dark-card" : "bg-gray-100"
          }`}
        >
          <i
            className={`ri-user-line text-4xl ${themeClasses.textSecondary}`}
          ></i>
        </div>
        <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`}>
          {t("users.noUsers")}
        </h3>
        <p className={`text-sm ${themeClasses.textSecondary} text-center`}>
          {t("users.noUsersDescription")}
        </p>
      </div>
    );
  }

  if (selectedView === "grid") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((user) => (
          <UserGridCard
            key={user.id}
            user={user}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onDetails={onDetails}
            onEdit={onEdit}
            onDelete={onDelete}
            onHardDelete={onHardDelete}
            onActivate={onActivate}
            onChangePassword={onChangePassword}
            isAdminReseller={isAdminReseller}
            isSelectionMode={isSelectionMode}
            isSelected={selectedIds.has(user.id)}
            onToggleSelection={onToggleSelection}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {users.map((user) => (
        <UserListRow
          key={user.id}
          user={user}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onDetails={onDetails}
          onEdit={onEdit}
          onDelete={onDelete}
          onHardDelete={onHardDelete}
          onActivate={onActivate}
          onChangePassword={onChangePassword}
          isAdminReseller={isAdminReseller}
          isSelectionMode={isSelectionMode}
          isSelected={selectedIds.has(user.id)}
          onToggleSelection={onToggleSelection}
        />
      ))}
    </div>
  );
}
